import { useEffect, useState, useRef } from "react";
import {
  Box,
  Text,
  Input,
  Button,
  Flex,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import { useDebouncedCallback } from "use-debounce";
import { useSession } from "next-auth/react";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { CloseIcon } from "@chakra-ui/icons";
import { useOnClickOutside } from "utils/helpers";
import { textStyle } from "theme/names";

interface Props {
  addPeople: (people: any[]) => void;
}

export function AddFromList({ addPeople }: Props) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState<boolean>(false);
  const [collectionOfLists, setCollectionOfLists] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [lists, setLists] = useState<any>();
  const { data: session } = useSession() ?? {}

  const suggestionBoxRef = useRef(null);

  useOnClickOutside(suggestionBoxRef, async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setIsSuggestionOpen(false);
  });

  const searchListSuggestion = async (listName: string) => {
    let contactLists: any;
    if (!lists) {
      const db = firebaseClient.firestore();
      const data = await db
        .collection("users")
        .doc(getAccount(session)?.id)
        .get();

      contactLists = data.data()?.contactLists;
      setLists(contactLists);
    } else {
      contactLists = lists;
    }

    if (!contactLists || contactLists.length === 0) {
      return;
    }

    const suggest = contactLists.filter((c) =>
      c.name.toUpperCase().includes(listName.toUpperCase())
    );

    setSuggestions(suggest);
  };

  const addPeopleFromSelectedList = async (list) => {
    if (collectionOfLists.some((l) => l.id === list.id)) {
      toast.error("Already added this list");
      setSearchTerm("");
      return;
    }

    const db = firebaseClient.firestore();
    const contactDocs = await db
      .collection("users")
      .doc(getAccount(session)?.id)
      .collection("contacts")
      .where("lists", "array-contains", list.id)
      .get();

    let contacts: any[] = [];
    contactDocs.forEach((c) => {
      contacts.push(c.data());
    });

    if (contacts.length === 0) {
      toast.error("No people to add! List does not have any contacts yet");
      return;
    }

    addPeople(contacts);
    setSearchTerm("");
    setCollectionOfLists([...collectionOfLists, list]);
  };

  const handleClick = async () => {
    try {
      if (!searchTerm.trim()) {
        toast.error("Please enter a list name");
        return;
      }
      setIsLoading(true);

      const db = firebaseClient.firestore();
      const data = await db
        .collection("users")
        .doc(getAccount(session)?.id)
        .get();

      const contactLists = data.data()?.contactLists;

      if (!contactLists || contactLists.length === 0) {
        toast.error("You don't have any lists");
        return;
      }

      const foundList = contactLists.filter((c) => c.name === searchTerm)[0];
      if (!foundList) {
        toast.error(`No list found with name: ${searchTerm}`);
        return;
      }

      addPeopleFromSelectedList(foundList);
    } catch (err) {
      console.log("Error in adding people form the list", err);
      toast.error("Error in adding people from the list" + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickToAdd = (list) => {
    try {
      setIsLoading(true);
      addPeopleFromSelectedList(list);
      setSuggestions([]);
      setIsSuggestionOpen(false);
    } catch (err) {
      console.log("Error in adding people form the list", err);
      toast.error("Error in adding people from the list" + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const debounced = useDebouncedCallback(searchListSuggestion, 400);

  return (
    <Box mt="4" mb="12">
      <Text textStyle={textStyle["h4.bold.standard"]} >
        Add from list
      </Text>
      <Flex mt="5" alignItems="center" position="relative">
        <Input
          w="sm"
          value={searchTerm}
          placeholder="Search for a list created in Taplio"
          borderRadius="lg"
          onFocus={(e) => {
            searchListSuggestion(e.target.value);
            setIsSuggestionOpen(true);
          }}
          onChange={(e) => {
            const value = e.target.value;
            debounced(value);
            setIsSuggestionOpen(true);
            setSearchTerm(e.target.value);
          }}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              handleClick();
            }
          }}
          isDisabled={isLoading}
        />
        {isSuggestionOpen && suggestions.length > 0 && (
          <Box
            position="absolute"
            top="10"
            width="100%"
            maxW="350px"
            border="1px"
            borderRadius="lg"
            zIndex="10"
            borderColor={useColorModeValue("border.lightMode.light", "border.darkMode.light")}
            boxShadow="md"
            background={useColorModeValue("white", "#1E1E1E")}
            ref={suggestionBoxRef}
          >
            {suggestions.map((s) => (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                p="2"
                px="3"
                cursor="pointer"
                _hover={{
                  background: useColorModeValue("gray.100", "whiteAlpha.100"),
                }}
                onClick={() => handleClickToAdd(s)}
              >
                <Text textStyle={textStyle["body.medium.light"]} maxW="280px" noOfLines={1} wordBreak={"break-all"}>
                  {s.name}
                </Text>
                <Text textStyle={textStyle["body.medium.light"]} color="gray" fontSize="xs">
                  Click to add
                </Text>
              </Flex>
            ))}
          </Box>
        )}
        <Button ml="4" onClick={handleClick} isLoading={isLoading} variant="primary">
          Add
        </Button>
      </Flex>
      <Stack mt="3" ml="3">
        {collectionOfLists.map((l) => (
          <Flex key={l.id} alignItems="center">
            <Text textStyle={textStyle["body.medium.light"]} fontWeight="bold">{l.name}</Text>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
